<template>
  <div
    v-if="position.isInCashSettlementDay || position.isInPhysicalSettlementDay"
    class="mt-4"
  >
    <div
      v-if="settlementDescription"
      class="flex items-center justify-start bg-neutral-secondary p-2 rounded-md"
    >
      <NegarErrorLightIcon size="16" class="ml-2 text-warning-primary" />

      <NText
        class="text-neutral-primary self-end"
        variant="caption1"
        weight="medium"
      >
        {{ settlementDescription }}
      </NText>
    </div>

    <div v-else-if="position.isInCashSettlementDay" class="w-full flex gap-3">
      <template v-if="position.cashSettlementRequestQuantity">
        <NButton
          variant="fill"
          color="green"
          class="flex-1"
          size="small"
          @click="openCashSettlementDialog(position, true)"
        >
          <NText variant="caption1" weight="bold">
            {{ t('option.editCashSettlement') }}
          </NText>
        </NButton>

        <NButton
          variant="outline"
          color="red"
          class="flex-1"
          size="small"
          @click="
            handleOpenDeleteSettlementDialog(position, true, $event.target)
          "
        >
          <NText variant="caption1" weight="bold">
            {{ t('option.cancelCashSettlement') }}
          </NText>
        </NButton>
      </template>

      <NButton
        v-else
        variant="fill"
        color="green"
        class="flex-1"
        size="small"
        @click="openCashSettlementDialog(position)"
      >
        <NText variant="caption1" weight="bold">
          {{ t('option.cashSettlement') }}
        </NText>
      </NButton>
    </div>

    <div
      v-else-if="position.isInPhysicalSettlementDay"
      class="w-full flex gap-3"
    >
      <template v-if="position.physicalSettlementRequestQuantity">
        <NButton
          variant="fill"
          color="green"
          class="flex-1"
          size="small"
          @click="openPhysicalSettlementDialog(position, true)"
        >
          <NText variant="caption1" weight="bold">
            {{ t('option.editPhysicalSettlement') }}
          </NText>
        </NButton>

        <NButton
          ref="deleteSettlementButtonRef"
          variant="outline"
          color="red"
          class="flex-1"
          size="small"
          @click="
            handleOpenDeleteSettlementDialog(position, false, $event.target)
          "
        >
          <NText variant="caption1" weight="bold">
            {{ t('option.cancelPhysicalSettlement') }}
          </NText>
        </NButton>
      </template>

      <NButton
        v-else
        variant="fill"
        color="green"
        class="flex-1"
        size="small"
        @click="openPhysicalSettlementDialog(position)"
      >
        <NText variant="caption1" weight="bold">
          {{ t('option.physicalSettlement') }}
        </NText>
      </NButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { NegarErrorLightIcon } from '@negar/icons';
import { getTimeDifference, sleep } from '@libs/utils';
import { useDialog, useToast } from '@negar/core';
import { useQueryClient } from '@libs/use';
import {
  OPTION_QUERY_KEYS,
  POSITIONS_SIDE,
  PositionItemWithPrices,
  TUseOptionPositionDetailMapperReturn,
  useDeleteCashSettlementQuery,
  useDeletePhysicalSettlementQuery,
} from '@core/option';

import { useOptionDialogs } from '@/modules/option';
import { useGetServerClock } from '@core/order/src';

type TPositionInfoDialogProps = {
  position: TUseOptionPositionDetailMapperReturn;
};

const props = defineProps<TPositionInfoDialogProps>();

const { time } = useGetServerClock();

const { t } = useI18n();
const toast = useToast();

const {
  openSellCashSettlementDialog,
  openBuyCashSettlementDialog,
  openDeleteSettlementDialog,
  openBuyPhysicalSettlementDialog,
} = useOptionDialogs();

const { remove: removeDialog } = useDialog();

const queryClient = useQueryClient();

const settlementDescription = computed(() => {
  if (
    props.position.isInCashSettlementDay &&
    !props.position.allowCashSettlementRequest
  ) {
    return t('option.settlementDescription.cashSettlementFinishedMessage');
  } else if (
    props.position.isInPhysicalSettlementDay &&
    !props.position.allowPhysicalSettlementRequest
  ) {
    return t('option.settlementDescription.physicalSettlementFinishedMessage');
  } else if (props.position.isCashSettlementForbidden) {
    return t('option.settlementDescription.cashSettlementForbiddenMessage');
  } else if (props.position.isSellContractForbidden) {
    return t('option.settlementDescription.sellContractForbiddenMessage');
  } else {
    return '';
  }
});

const { deleteCashSettlement, isLoadingDeleteCashSettlement } =
  useDeleteCashSettlementQuery({
    async onSuccess() {
      toast.neutral({
        message: t('option.settlementMessage.delete', {
          settlementType: t('option.settlementRequest.cash'),
        }),
        duration: 3000,
      });

      removeDialog();

      await sleep(3000);

      queryClient.invalidateQueries(OPTION_QUERY_KEYS.positions);
    },
    onReject(error) {
      const message = error.response?.data[0].message;

      if (message) {
        toast.negative({
          message,
          duration: 3000,
        });
      } else {
        toast.negative({
          message: t('option.settlementMessage.error'),
          duration: 3000,
        });
      }
    },
  });

const { deletePhysicalSettlement, isLoadingDeletePhysicalSettlement } =
  useDeletePhysicalSettlementQuery({
    async onSuccess() {
      toast.neutral({
        message: t('option.settlementMessage.delete', {
          settlementType: t('option.settlementRequest.physical'),
        }),
        duration: 3000,
      });

      removeDialog();

      await sleep(3000);

      queryClient.invalidateQueries(OPTION_QUERY_KEYS.positions);
    },
    async onReject(error) {
      const message = error.response?.data[0].message;

      if (message) {
        toast.negative({
          message,
          duration: 3000,
        });
      } else {
        toast.negative({
          message: t('option.settlementMessage.error'),
          duration: 3000,
        });
      }
    },
  });

const openCashSettlementDialog = (
  position: PositionItemWithPrices,
  isEditMode = false
) => {
  if (position.positionSide === POSITIONS_SIDE.buy) {
    return openBuyCashSettlementDialog({
      isEditMode,
      positionSettlementModel: position,
    });
  }

  openSellCashSettlementDialog({
    isEditMode,
    positionSettlementModel: position,
  });
};

const openPhysicalSettlementDialog = (
  position: PositionItemWithPrices,
  isEditMode = false
) => {
  return openBuyPhysicalSettlementDialog({
    isEditMode,
    positionPhysicalSettlementModel: position,
  });
};

const handleOpenDeleteSettlementDialog = (
  position: PositionItemWithPrices,
  isInCashSettlementDay,
  deleteSettlementButtonRef: HTMLElement
) => {
  openDeleteSettlementDialog({
    position: position,
    handleDelete: isInCashSettlementDay
      ? deleteCashSettlement
      : deletePhysicalSettlement,
    referenceElementRef: deleteSettlementButtonRef,
    isInCashSettlementDay: isInCashSettlementDay,
    loading: computed(
      () =>
        isLoadingDeletePhysicalSettlement.value ||
        isLoadingDeleteCashSettlement.value
    ),
  });
};

const calcSettlementDifferenceDays = (date: string) =>
  getTimeDifference({
    startTime: new Date(date as string).getTime(),
    endTime: new Date(time.value).getTime(),
  }).differenceInDays;
</script>
